/**
 * AppSidebar
 * Desktop (lg+) navigation for the authenticated app, rendered by Topbar.
 * Builds the role-based items and hands them to the shared Sidebar.
 */

import { useLocation } from 'react-router-dom';
import { LogIn, LogOut, Shield, Users } from 'lucide-react';
import { BetaBadge } from '@/shared/components/BetaBadge';
import { Sidebar, type SidebarItem } from '@/shared/components/Sidebar';
import type { AuthUser } from '@/core/auth/types';
import type { NavigationItem } from '../config/navigationItems';

/**
 * Renders navigation item badge based on badge type
 */
const renderNavBadge = (badge: string | undefined) => {
  if (!badge) return null;

  switch (badge) {
    case 'beta':
      return <BetaBadge variant="dark" className="ml-0" />;
    default:
      return (
        <span className="ml-1 px-2 py-0.5 text-xs rounded-full bg-red-500 text-white">
          {badge}
        </span>
      );
  }
};

type AppSidebarProps = {
  /** null renders the guest variant (Get Started / Login) */
  user: AuthUser | null;
  homeRoute: string;
  roleDisplayName: string;
  navItems: NavigationItem[];
  isAdmin: boolean;
  fullName: string;
  initials: string;
  onLogout: () => void;
};

export const AppSidebar = ({
  user,
  homeRoute,
  roleDisplayName,
  navItems,
  isAdmin,
  fullName,
  initials,
  onLogout,
}: AppSidebarProps) => {
  const { pathname } = useLocation();

  if (!user) {
    return (
      <Sidebar
        homeHref={homeRoute}
        items={[]}
        cta={{ label: 'Get Started', href: '/register' }}
        footerItems={[{ key: 'login', label: 'Login', icon: LogIn, href: '/login' }]}
      />
    );
  }

  const items: SidebarItem[] = navItems.map((item) => ({
    key: `${item.label}-${item.href}`,
    label: item.label,
    icon: item.icon,
    href: item.href,
    active: pathname === item.href,
    badge: renderNavBadge(item.badge),
  }));

  // Admin button - only visible for admin users (T042 - US1)
  if (isAdmin) {
    items.push({ key: 'admin', label: 'Admin', icon: Shield, href: '/admin', active: pathname.startsWith('/admin') });
  }

  // Feature 075: owners manage their sub-delegates;
  // hidden for SUB_DELEGATE accounts (no sub-sub-delegation)
  if (user.role !== 'SUB_DELEGATE') {
    items.push({
      key: 'sub-delegates',
      label: 'Manage sub-delegates',
      icon: Users,
      href: '/workspace/sub-delegates',
      active: pathname === '/workspace/sub-delegates',
    });
  }

  return (
    <Sidebar
      homeHref={homeRoute}
      label={roleDisplayName}
      items={items}
      footerItems={[{ key: 'logout', label: 'Logout', icon: LogOut, onClick: onLogout }]}
      profile={{
        name: fullName,
        initials,
        photoUrl: user.profilePhoto,
        href: '/profile',
        active: pathname === '/profile',
      }}
    />
  );
};
